/**
 * Render a cron expression as a short, human-readable Italian phrase for the ETL
 * pipelines list ("ogni notte alle 02:00", "ogni 15 minuti", "lun alle 09:00").
 *
 * Deliberately covers only the COMMON shapes a scheduling UI produces — anything
 * exotic falls back to the raw cron string (never throws, never lies). This is a
 * display helper, not a full cron parser.
 */

const DOW = ["dom", "lun", "mar", "mer", "gio", "ven", "sab"]; // 0=Sunday

function pad(n: string): string {
  return n.length === 1 ? `0${n}` : n;
}

/** A 5-field cron → Italian phrase, or the raw string when it's not a simple shape. */
export function describeCron(cron: string | null, tz?: string | null): string | null {
  if (!cron) return null;
  const raw = cron.trim();
  const parts = raw.split(/\s+/);
  if (parts.length !== 5) return raw;
  const [min, hour, dom, mon, dow] = parts;
  const suffix = tz ? ` (${tz})` : "";

  const atTime = (h: string, m: string) => `alle ${pad(h)}:${pad(m)}`;

  // Every minute / every N minutes.
  if (min === "*" && hour === "*" && dom === "*" && mon === "*" && dow === "*") {
    return "ogni minuto";
  }
  const everyN = /^\*\/(\d+)$/.exec(min);
  if (everyN && hour === "*" && dom === "*" && mon === "*" && dow === "*") {
    return `ogni ${everyN[1]} minuti`;
  }
  // Hourly (at minute M).
  if (/^\d+$/.test(min) && hour === "*" && dom === "*" && mon === "*" && dow === "*") {
    return min === "0" ? "ogni ora" : `ogni ora al minuto ${min}`;
  }

  // From here on we need concrete minute + hour.
  if (!/^\d+$/.test(min) || !/^\d+$/.test(hour)) return raw;

  // Daily.
  if (dom === "*" && mon === "*" && dow === "*") {
    return `ogni giorno ${atTime(hour, min)}${suffix}`;
  }
  // Weekdays (Mon–Fri).
  if (dom === "*" && mon === "*" && (dow === "1-5")) {
    return `nei giorni feriali ${atTime(hour, min)}${suffix}`;
  }
  // A single weekday.
  if (dom === "*" && mon === "*" && /^[0-6]$/.test(dow)) {
    return `ogni ${DOW[Number(dow)]} ${atTime(hour, min)}${suffix}`;
  }
  // A day of the month.
  if (/^\d+$/.test(dom) && mon === "*" && dow === "*") {
    return `il giorno ${dom} di ogni mese ${atTime(hour, min)}${suffix}`;
  }

  return raw;
}
